import React, { useState, useEffect, useMemo } from "react";
import { database } from "../firebaseConfig";
import { ref, get } from "firebase/database";
import "./ProductCard.css";

const ProductCard = ({
    title,
    description,
    price,
    mrp,
    discount,
    rating,
    mediaUrl,
    mediaType = "image",
    metaTitle = "",
    metaDescription = "",
    metaKeywords = "",
    edit = false,
    editTitle = false,
    onEdit,
    onSave,
    onDelete,
    onClick,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [formState, setFormState] = useState({
        title,
        description,
        price,
        mrp,
        discount,
        rating,
        metaTitle,
        metaDescription,
        metaKeywords,
    });
    const [localCardConfig, setLocalCardConfig] = useState({});

    useEffect(() => {
        const fetchConfig = async () => {
            try {
                const snapshot = await get(ref(database, "cardConfig"));
                const fetched = snapshot.exists() ? snapshot.val() : {};
                setLocalCardConfig(fetched);
            } catch (error) {
                console.error("Error fetching config:", error);
                setLocalCardConfig({});
            }
        };
        fetchConfig();
    }, []);

    useEffect(() => {
        setFormState({
            title,
            description,
            price,
            mrp,
            discount,
            rating,
            metaTitle,
            metaDescription,
            metaKeywords,
        });
    }, [title, description, price, mrp, discount, rating, metaTitle, metaDescription, metaKeywords]);

    const mergedConfig = useMemo(() => localCardConfig, [localCardConfig]);

    const handleInputChange = (field, value) => {
        setFormState((prev) => ({ ...prev, [field]: value }));
    };

    const handleSave = () => {
        onSave?.(formState);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setFormState({
            title,
            description,
            price,
            mrp,
            discount,
            rating,
            metaTitle,
            metaDescription,
            metaKeywords,
        });
        setIsEditing(false);
    };

    const truncate = (text) =>
        typeof text === "string" && text.split(" ").length > 17
            ? text.split(" ").slice(0, 17).join(" ") + "..."
            : text;

    const renderTextField = (key, label, placeholder = "", extraStyle = {}) => (
        <input
            type="text"
            value={formState[key]}
            onChange={(e) => handleInputChange(key, e.target.value)}
            className={`edit-input product-${key}`}
            placeholder={placeholder || label}
            style={{ ...getStyle(key), ...extraStyle }}
        />
    );

    const getStyle = (key) => {
        const config = mergedConfig[key] || {};
        return {
            fontSize: config.fontSize || "1rem",
            textAlign: config.align || "left",
            color: config.color || "#000",
        };
    };

    const fields = [
        {
            key: "rating",
            content: formState.rating && (
                <p style={getStyle("rating")}>Rating: {formState.rating}</p>
            ),
        },
        {
            key: "description",
            content: formState.description && (
                // <p style={getStyle("description")}>{truncate(formState.description)}</p>
                <div></div>
            ),
        },
        {
            key: "priceBlock",
            content: (formState.price || formState.mrp) && (
                <div className="product-price-info">
                    {formState.mrp && (
                        <span
                            style={{
                                ...getStyle("price"),
                                textDecoration: "line-through",
                                marginLeft: "5px",
                            }}
                        >
                            ₹{formState.mrp}
                        </span>
                    )}
                    {formState.price && (
                        <span style={getStyle("price")}> ₹{formState.price}</span>
                    )}
                </div>
            ),
        },
        {
            key: "discount",
            content: formState.discount && (
                <span style={getStyle("discount")}>{formState.discount}% Off</span>
            ),
        },
        {
            key: "title",
            content: formState.title && (
                <div className="title-container" style={{ position: "relative" }}>
                    <h3 style={getStyle("title")}>{formState.title}</h3>
                    {edit && editTitle && (
                        <button className="edit-title-btn" onClick={() => setIsEditing(true)}>
                            ✎
                        </button>
                    )}
                </div>
            ),
        },
    ];

    return (
        <div
            className="product-card"
            onClick={onClick}
            style={{ backgroundColor: mergedConfig.cardBgColor || "#000" }}
        >
            <div className="media-container">
                {mediaType === "video" ? (
                    <video controls className="product-media">
                        <source src={mediaUrl} type="video/mp4" />
                        Your browser does not support the video tag.
                    </video>
                ) : (
                    <img src={mediaUrl} alt={formState.title} className="product-media" />
                )}
                {edit && (
                    <button className="edit-media-btn" onClick={onEdit}>
                        ✎
                    </button>
                )}
            </div>

            <div className="product-details">
                {isEditing ? (
                    <div className="product-text-fields">
                        {renderTextField("title", "Title")}
                        <textarea
                            value={formState.description}
                            onChange={(e) => handleInputChange("description", e.target.value)}
                            className="edit-input product-description"
                            placeholder="Description"
                            style={getStyle("description")}
                        />
                        {renderTextField("price", "Price")}
                        {renderTextField("mrp", "MRP", "", {
                            textDecoration: "line-through",
                            marginLeft: "5px",
                        })}
                        {renderTextField("discount", "Discount %")}
                        {renderTextField("rating", "Rating")}

                        {/* SEO Fields */}
                        {renderTextField("metaTitle", "Meta Title")}
                        <textarea
                            value={formState.metaDescription}
                            onChange={(e) => handleInputChange("metaDescription", e.target.value)}
                            className="edit-input product-metaDescription"
                            placeholder="Meta Description"
                        />
                        {renderTextField("metaKeywords", "Meta Keywords")}

                        <div className="edit-actions">
                            <button onClick={handleSave} className="save-btn">
                                Save
                            </button>
                            <button onClick={handleCancel} className="cancel-btn">
                                Cancel
                            </button>
                        </div>
                    </div>
                ) : (
                    <div className="product-text-fields">
                        {fields
                            .filter((f) => f.content)
                            .sort((a, b) => {
                                const orderA = mergedConfig[a.key]?.order ?? 0;
                                const orderB = mergedConfig[b.key]?.order ?? 0;
                                return orderA - orderB;
                            })
                            .map((f) => (
                                <React.Fragment key={f.key}>{f.content}</React.Fragment>
                            ))}
                    </div>
                )}
            </div>

            {onDelete && (
                <button className="delete-product-btn" onClick={onDelete}>
                    Delete
                </button>
            )}
        </div>
    );
};

export default ProductCard;
